import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { IChecklistTemplateSelectOption } from 'CorePackage/AccountModule/interfaces/IChecklistTemplateSelectOption';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import {
  knmChecklistTemplatesGet,
  knmRequirementTemplatesByKnoOrganizationIdAndSupervisionTypeIdGet,
} from 'KnmPackage/MainModule/actions/knmActions';
import {
  getKnmAvailableChecklistTemplatesSelectOptions,
  getKnmAvailableRequirementTemplatesSelectOptions,
  getKnmErpOrganizationFormValue,
  getKnmErpSupervisionTypeFormValue,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { SelectInput } from 'UIPackage/MainModule/components/SelectInput';
import { Button } from 'UIPackage/MainModule/components/Button';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IRequirementTemplateSelectOption } from 'CorePackage/AccountModule/interfaces/IRequirementTemplateSelectOption';
import { IKnmErpForm } from 'KnmErpPackage/MainModule/interfaces/IKnmErpForm';
import { IDictionaryKnoOrganizationSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { IDictionarySupervisionTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';

import styles from './KnmErpAddTemplatesModal.module.scss';

interface IKnmErpAddTemplatesModalProps {
  visible?: boolean;
  isChecklists?: boolean;
  formRequirements?: IKnmErpForm['requirements'];
  checklistTemplatesSelectOptions: IChecklistTemplateSelectOption[] | undefined;
  requirementTemplatesSelectOptions: IRequirementTemplateSelectOption[] | undefined;
  erpErvkKnoOrganizationFormValue: IDictionaryKnoOrganizationSelectOption | undefined;
  erpErvkSupervisionTypeFormValue: IDictionarySupervisionTypeSelectOption | undefined;
  onApply: (template: IChecklistTemplateSelectOption | IRequirementTemplateSelectOption) => void;
  onClose: () => void;
}

const KnmErpAddTemplatesModalWithoutStore = ({
  isChecklists,
  visible,
  formRequirements,
  checklistTemplatesSelectOptions,
  requirementTemplatesSelectOptions,
  erpErvkKnoOrganizationFormValue,
  erpErvkSupervisionTypeFormValue,
  onApply,
  onClose,
}: IKnmErpAddTemplatesModalProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const dispatchKnmChecklistTemplatesGet = useCallback(
    () =>
      dispatch(
        isChecklists
          ? knmChecklistTemplatesGet()
          : knmRequirementTemplatesByKnoOrganizationIdAndSupervisionTypeIdGet({
              knoOrganizationId: erpErvkKnoOrganizationFormValue?.id,
              knmSupervisionTypeId: erpErvkSupervisionTypeFormValue?.id,
            }),
      ),
    [dispatch, isChecklists, erpErvkKnoOrganizationFormValue, erpErvkSupervisionTypeFormValue],
  );

  const disablingRequirementTemplatesSelectOptions: IRequirementTemplateSelectOption[] | undefined = useMemo(
    () =>
      requirementTemplatesSelectOptions?.map(item => ({
        ...item,
        ...(formRequirements?.some(formRequirement => formRequirement?.template?.id === item.id) && {
          isDisabled: true,
        }),
      })),
    [requirementTemplatesSelectOptions, formRequirements],
  );

  useEffect(() => {
    if (visible) {
      dispatchKnmChecklistTemplatesGet();
    }
  }, [visible, isChecklists, dispatchKnmChecklistTemplatesGet]);

  const [selectValue, setSelectValue] = useState<IChecklistTemplateSelectOption | null>(null);

  const handleSelectChange = useCallback((value: IChecklistTemplateSelectOption) => setSelectValue(value), []);

  const handleAdd = useCallback(() => {
    onClose();

    if (selectValue) {
      onApply(selectValue);
    }
  }, [onApply, onClose, selectValue]);

  const handleAfterClose = useCallback(() => setSelectValue(null), []);

  return (
    <Modal visible={visible} modalBodyClassName={styles.ModalBody} onClose={onClose} afterClose={handleAfterClose}>
      <ModalHeader
        title={isChecklists ? 'Добавление проверочного листа' : 'Добавление обязательного требования'}
        sizeText="small"
      />
      <ModalBody size="content" border="bottom">
        <div className={styles.Label}>Выберите шаблон</div>
        <SelectInput
          name="template"
          options={isChecklists ? checklistTemplatesSelectOptions : disablingRequirementTemplatesSelectOptions}
          value={selectValue}
          isSearchable
          onChange={handleSelectChange}
        />
      </ModalBody>
      <ModalActions>
        <Button text="Добавить" color="primary" size="medium" disabled={!selectValue} onClick={handleAdd} />
        <Button text="Закрыть" size="medium" onClick={onClose} />
      </ModalActions>
    </Modal>
  );
};

const mapStateToProps = (state: IRootState) => ({
  checklistTemplatesSelectOptions: getKnmAvailableChecklistTemplatesSelectOptions(state),
  requirementTemplatesSelectOptions: getKnmAvailableRequirementTemplatesSelectOptions(state),
  erpErvkKnoOrganizationFormValue: getKnmErpOrganizationFormValue(state),
  erpErvkSupervisionTypeFormValue: getKnmErpSupervisionTypeFormValue(state),
});

export const KnmErpAddTemplatesModal = connect(mapStateToProps)(KnmErpAddTemplatesModalWithoutStore);
